import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useI18nStore, useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { searchPlaces, type GeocodeHit } from '../lib/mapbox-geocode';
import { env } from '@/env';

// Frontend (publishable) token, injected at runtime (window.__ENV__). Restrict
// it by URL in the MapBox dashboard. Without it the picker degrades to a hint.
const TOKEN = env.mapboxToken;

export interface LatLng {
  latitude: number;
  longitude: number;
}

// Fallback view when the restaurant has no pin yet (Berlin) - the owner pans/taps.
const DEFAULT_CENTER: [number, number] = [13.405, 52.52];

/**
 * A map with a single draggable pin plus an address search box. Searching flies
 * to and drops the pin; dragging the pin or tapping the map also moves it. Each
 * change emits the new coordinates. Controlled by `value`.
 */
export function MapboxLocationPicker({
  value,
  onChange,
  initialQuery,
  autoSearch = false,
}: {
  value: LatLng | null;
  onChange: (v: LatLng) => void;
  /** Seed the search box with this text on mount (e.g. the business-profile
   *  address), so the operator doesn't re-type an address they already entered. */
  initialQuery?: string;
  /** When true, run the search for `initialQuery` once on mount. Pass this only
   *  when the restaurant has no saved pin yet — an already-pinned location keeps
   *  its pin and just shows the address text. */
  autoSearch?: boolean;
}) {
  const mp = useT().orderSetup.mapPicker;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const didCenterRef = useRef(false);

  const locale = useI18nStore((s) => s.locale);
  const country = useRestaurantStore((s) => s.restaurantConfig?.country) ?? undefined;
  const [query, setQuery] = useState(() => initialQuery ?? '');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<GeocodeHit[]>([]);

  useEffect(() => {
    if (!TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = TOKEN;
    const start: [number, number] = value ? [value.longitude, value.latitude] : DEFAULT_CENTER;
    didCenterRef.current = value != null;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: start,
      zoom: value ? 14 : 11,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const marker = new mapboxgl.Marker({ draggable: true }).setLngLat(start).addTo(map);
    marker.on('dragend', () => {
      const { lng, lat } = marker.getLngLat();
      onChangeRef.current({ latitude: lat, longitude: lng });
    });
    map.on('click', (e: mapboxgl.MapMouseEvent) => {
      marker.setLngLat(e.lngLat);
      onChangeRef.current({ latitude: e.lngLat.lat, longitude: e.lngLat.lng });
    });

    mapRef.current = map;
    markerRef.current = marker;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // Init once; external value changes handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the saved location arrives after init, center the map on it once.
  useEffect(() => {
    if (!value || !mapRef.current || !markerRef.current) return;
    markerRef.current.setLngLat([value.longitude, value.latitude]);
    if (!didCenterRef.current) {
      mapRef.current.flyTo({ center: [value.longitude, value.latitude], zoom: 14 });
      didCenterRef.current = true;
    }
  }, [value]);

  // Drop the pin on a hit (move marker, fly to it, emit coords) WITHOUT touching
  // the results list — so an auto-pin can leave the candidate list on screen.
  const dropPin = (hit: GeocodeHit) => {
    markerRef.current?.setLngLat([hit.longitude, hit.latitude]);
    mapRef.current?.flyTo({ center: [hit.longitude, hit.latitude], zoom: 16 });
    didCenterRef.current = true;
    onChangeRef.current({ latitude: hit.latitude, longitude: hit.longitude });
  };

  const runSearch = async (): Promise<GeocodeHit[]> => {
    if (!query.trim() || searching) return [];
    setSearching(true);
    setSearchError(null);
    setResults([]);
    const center = mapRef.current?.getCenter();
    const hits = await searchPlaces(query, {
      proximity: center ? [center.lng, center.lat] : undefined,
      language: locale,
      country,
    });
    setSearching(false);
    if (!hits.length) {
      setSearchError(mp.noResults);
      return [];
    }
    setResults(hits);
    return hits;
  };

  // Manual pick from the results list: pin AND collapse the list (the operator
  // made their choice).
  const pick = (hit: GeocodeHit) => {
    dropPin(hit);
    setResults([]);
  };

  // One-shot: when the restaurant has no saved pin yet, auto-run the search for
  // the address already typed in the business profile (seeded into `query`) and
  // auto-drop the pin on the top hit — but KEEP the results list visible so the
  // operator can pick a different candidate or drag the pin if the top hit is off.
  // Never auto-saves; Save stays manual.
  const didAutoSearchRef = useRef(false);
  useEffect(() => {
    if (didAutoSearchRef.current || !autoSearch || !query.trim()) return;
    didAutoSearchRef.current = true;
    void runSearch().then((hits) => {
      if (hits.length) dropPin(hits[0]);
    });
    // runSearch/dropPin read state from closure; fire once, hence the ref guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch]);

  if (!TOKEN) {
    return (
      <div style={{ padding: 16, color: 'var(--f7-list-item-subtitle-text-color)' }}>
        {mp.unavailable}
      </div>
    );
  }

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void runSearch();
        }}
        style={{ display: 'flex', gap: 8, padding: '8px 12px' }}
      >
        <input
          type="search"
          placeholder={mp.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            flex: 1,
            height: 36,
            padding: '0 12px',
            borderRadius: 8,
            border: '1px solid var(--f7-list-item-border-color, #ddd)',
            background: 'var(--f7-page-bg-color, #fff)',
            color: 'inherit',
          }}
        />
        <button
          type="submit"
          disabled={searching || !query.trim()}
          style={{
            height: 36,
            padding: '0 14px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--f7-theme-color, #007aff)',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          {searching ? '…' : mp.search}
        </button>
      </form>
      {searchError ? (
        <div style={{ padding: '0 12px 8px', color: '#c8780a', fontSize: 13 }}>{searchError}</div>
      ) : null}
      {results.length ? (
        <div style={{ padding: '0 12px 8px' }}>
          <div
            style={{
              fontSize: 12,
              color: 'var(--f7-list-item-subtitle-text-color)',
              marginBottom: 4,
            }}
          >
            {mp.pickHint}
          </div>
          <div
            style={{
              border: '1px solid var(--f7-list-item-border-color, #ddd)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {results.map((hit, i) => (
              <button
                key={`${hit.latitude},${hit.longitude},${i}`}
                type="button"
                onClick={() => pick(hit)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  borderTop: i ? '1px solid var(--f7-list-item-border-color, #eee)' : 'none',
                  background: 'transparent',
                  color: 'inherit',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                {hit.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div ref={containerRef} style={{ width: '100%', height: 320 }} />
    </div>
  );
}
